/*************************************************

* BAKER HUGHES COMPANY CONFIDENTIAL

* Unpublished work copyright 2025 Baker Hughes Company.  All rights reserved.

*

* This computer code is proprietary and highly confidential to Baker Hughes Company

* and/or its affiliate(s). It may not be used, disclosed, modified, transferred,

* or reproduced without prior written consent, and must be returned on demand.

**************************************************/
/* Copied from Backstage repo plugins\catalog-react\src\components\UserListPicker\useAllEntitiesCount.ts  and customized */

import { QueryEntitiesInitialRequest } from '@backstage/catalog-client';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef, useEntityList } from '@backstage/plugin-catalog-react';
import { compact, isEqual } from 'lodash';
import { useMemo, useRef } from 'react';
import useAsync from 'react-use/lib/useAsync';
import { reduceCatalogFilters } from '../../../../utils/filters';

export function useAllEntitiesCount() {
  const catalogApi = useApi(catalogApiRef);
  const { filters } = useEntityList();

  const prevRequest = useRef<QueryEntitiesInitialRequest>();
  const request = useMemo(() => {
    const { user, ...allFilters } = filters;
    const compacted = compact(Object.values(allFilters));
    const catalogFilters = reduceCatalogFilters(compacted);
    const newRequest: QueryEntitiesInitialRequest = {
      ...catalogFilters,
      limit: 0,
    };

    if (Object.keys(catalogFilters.filter).length === 0) {
      prevRequest.current = undefined;
      return prevRequest.current;
    }

    if (isEqual(newRequest, prevRequest.current)) {
      return prevRequest.current;
    }
    prevRequest.current = newRequest;
    return newRequest;
  }, [filters]);

  const { value: count, loading } = useAsync(async () => {
    if (request === undefined) {
      return 0;
    }
    const { totalItems } = await catalogApi.queryEntities(request);

    return totalItems;
  }, [request]);

  return { count, loading };
}
